import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faClipboardList,
  faCheckCircle,
  faClock,
  faEye,
  faHashtag,
  faUser,
  faMoneyBillWave,
  faUsers,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  Table,
  InputGroup,
  Form,
  Button,
  Badge,
  Modal,
  Row,
  Col,
  Spinner,
} from "@themesberg/react-bootstrap";
import { toast } from "react-toastify";
import {
  adminGetTransactionHistory,
  adminGetTransactionById,
} from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const mapTransactionItem = (item) => ({
  apiId: item?.transaction_id ?? null,
  transactionId: String(item?.transaction_id ?? "N/A"),
  userId: String(item?.user_id ?? "N/A"),
  userName: item?.user_name || "N/A",
  date: item?.date || "N/A",
  amount: Number(item?.amount || 0),
  description: item?.description || "N/A",
  paymentMethod: item?.payment_method || "N/A",
  status: item?.status || "N/A",
  customUserId: item?.custom_user_id || "N/A",
});

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatStatus = (value) => {
  if (!value) return "N/A" || "-";
  const str = String(value).trim();
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const formatDateTime = (value) => {
  if (!value) return "N/A" || "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: value.includes(":") || value.includes("T") ? "2-digit" : undefined,
    minute: value.includes(":") || value.includes("T") ? "2-digit" : undefined,
    hour12: false,
  }).format(date);
};

const getStatusBadgeStyle = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "success":
      return { background: "#10b981", color: "#ffffff" };
    case "completed":
      return { background: "#10b981", color: "#ffffff" };
    case "pending":
      return { background: "#f59e0b", color: "#ffffff" };
    case "failed":
      return { background: "#ef4444", color: "#ffffff" };
    case "refunded":
      return { background: "#6366f1", color: "#ffffff" };
    default:
      return { background: "#6b7280", color: "#ffffff" };
  }
};

const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getPageItems = (pagination) => {
  const totalPages = pagination.last_page || 1;
  const current = pagination.current_page || 1;

  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (current <= 2) {
    return [1, 2, 3, "...", totalPages];
  }

  if (current >= totalPages - 1) {
    return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", current - 1, current, current + 1, "...", totalPages];
};

export default function AdminTransactionHistory() {
  const [search, setSearch] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const searchDebounceRef = useRef(null);
  const firstSearchRenderRef = useRef(true);

  const fetchTransactions = async (page = 1, perPage = 10, searchTerm = "") => {
    try {
      setLoading(true);

      const response = await adminGetTransactionHistory({
        page,
        per_page: perPage,
        search: searchTerm,
      });

      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiTransactions = Array.isArray(apiData?.transactions)
        ? apiData.transactions
        : [];
      const apiPagination = apiData?.pagination || {};

      setTransactions(apiTransactions.map(mapTransactionItem));
      setPagination({
        current_page: Number(apiPagination.current_page ?? page) || page,
        last_page: Number(apiPagination.last_page ?? 1) || 1,
        per_page: Number(apiPagination.per_page ?? perPage) || perPage,
        total:
          Number(apiPagination.total ?? apiTransactions.length) ||
          apiTransactions.length,
      });
    } catch (error) {
      console.error("Failed to fetch transaction history:", error);
      toast.error(
        error?.response?.data?.message || "Unable to fetch transaction history."
      );
      setTransactions([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(1, 10, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstSearchRenderRef.current) {
      firstSearchRenderRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchTransactions(1, pagination.per_page, search.trim());
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const clearSearchDebounce = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const getShowingRange = () => {
    const total = pagination.total || 0;
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const { start, end } = getShowingRange();

  const openDetail = async (item) => {
    try {
      setSelectedTransaction(item);
      setShowDetailModal(true);
      setLoadingDetail(true);

      if (!item?.apiId) return;

      const response = await adminGetTransactionById(item.apiId);
      const apiData = response?.data?.data || response?.data || {};
      setSelectedTransaction(mapTransactionItem(apiData));
    } catch (error) {
      console.error("Failed to fetch transaction detail:", error);
      toast.error(
        error?.response?.data?.message || "Unable to fetch transaction details."
      );
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedTransaction(null);
    setLoadingDetail(false);
  };

  const handleExport = () => {
    toast.info("Export clicked");
  };

  return (
    <>
      <div className="transaction-page">
        <div className="transaction-header py-4">
          <div className="transaction-heading-wrap">
            {/* <h5 className="transaction-title mb-1">Transaction History</h5> */}
            <h2 className="h4 mb-1 transaction-title">Transaction History</h2>
          </div>

          <div className="transaction-header-actions">
            <InputGroup className="transaction-search">
              <InputGroup.Text className="bg-white">
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ boxShadow: "none" }}
              />
            </InputGroup>
          </div>
        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-4">
            <div className="transaction-list-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div className="transaction-list-title-wrap">
                <h5 className="mb-1">All Transactions</h5>
              </div>

              {/* <Button
                type="button"
                style={ACTIVE_BUTTON_STYLE}
                className="transaction-action-btn fw-semibold"
                onClick={handleExport}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Export
              </Button> */}
            </div>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0 transaction-table">
                <thead>
                  <tr>
                    <th className="fw-semibold">Sr No</th>
                    <th>Date</th>
                    <th>Transaction ID</th>
                    <th>User</th>
                    <th>Customer ID</th>
                    <th>Payment Method</th>
                    <th className="text-end">Amount</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-5">
                        Loading transaction history...
                      </td>
                    </tr>
                  ) : transactions.length > 0 ? (
                    transactions.map((item, index) => (
                      <tr key={item.apiId ?? item.transactionId}>
                        <td className="fw-semibold">{start + index}</td>
                        <td className="text-muted">{formatDateTime(item.date)}</td>
                        <td className="fw-semibold">{item.transactionId || "N/A" || "-"}</td>
                        <td>
                          <div className="fw-semibold">{item.userName || "N/A" || "-"}</div>
                        </td>
                        <td>{item.customUserId || "N/A" || "-"}</td>
                        <td>{capitalize(item.paymentMethod || "N/A" || "-")}</td>
                        <td className="text-end fw-semibold">
                          {formatCurrency(item.amount)}
                        </td>
                        <td className="text-center">
                          <Badge
                            className="rounded-pill d-inline-flex align-items-center justify-content-center"
                            style={{
                              ...getStatusBadgeStyle(item.status),
                              minWidth: "5.5rem",
                              height: "2rem",
                              padding: "0 12px",
                            }}
                          >
                            {capitalize(item.status || "N/A")}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Button
                            type="button"
                            variant="link"
                            className="p-0 text-decoration-none"
                            onClick={() => openDetail(item)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-5">
                        No transactions found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="text-muted small">
                Showing {start} to {end} of {pagination.total} entries
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page <= 1 || loading}
                  onClick={() => {
                    clearSearchDebounce();
                    fetchTransactions(
                      Math.max(1, pagination.current_page - 1),
                      pagination.per_page,
                      search.trim()
                    );
                  }}
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>

                {getPageItems(pagination).map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-muted px-1">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={item}
                      type="button"
                      variant="outline-primary"
                      disabled={loading}
                      onClick={() => {
                        clearSearchDebounce();
                        fetchTransactions(item, pagination.per_page, search.trim());
                      }}
                      className={`course-page-btn ${item === pagination.current_page
                        ? "course-page-btn-active"
                        : ""
                        }`}
                    >
                      {item}
                    </Button>
                  )
                )}

                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page >= pagination.last_page || loading}
                  onClick={() => {
                    clearSearchDebounce();
                    fetchTransactions(
                      Math.min(pagination.last_page, pagination.current_page + 1),
                      pagination.per_page,
                      search.trim()
                    );
                  }}
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showDetailModal} onHide={closeDetail} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Transaction Details</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedTransaction ? (
            <>
              {loadingDetail ? (
                <div className="text-center text-muted py-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading transaction details...
                </div>
              ) : null}

              <Row className="g-3">
                <Col md={6}>
                  <div className="detail-box">
                    <div className="text-muted small">Transaction ID</div>
                    <div className="fw-semibold">
                      {selectedTransaction.transactionId || "N/A" || "-"}
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="detail-box">
                    <div className="text-muted small">User</div>
                    <div className="fw-semibold">{selectedTransaction.userName || "N/A" || "-"}</div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="detail-box">
                    <div className="text-muted small">Date</div>
                    <div className="fw-semibold">
                      {formatDateTime(selectedTransaction.date || "N/A" || "-")}
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="detail-box">
                    <div className="text-muted small">Amount</div>
                    <div className="fw-semibold">
                      {formatCurrency(selectedTransaction.amount || "N/A" || "-")}
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="detail-box">
                    <div className="text-muted small">Payment Method</div>
                    <div className="fw-semibold">
                      {capitalize(selectedTransaction.paymentMethod || "N/A" || "-")}
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="detail-box">
                    <div className="text-muted small">Status</div>
                    <div className="fw-semibold">
                      {capitalize(selectedTransaction.status || "N/A" || "-")}
                    </div>
                  </div>
                </Col>

                <Col md={12}>
                  <div className="detail-box">
                    <div className="text-muted small">Description</div>
                    <div className="fw-semibold">
                      {selectedTransaction.description || "N/A" || "-"}
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          ) : null}
        </Modal.Body>

        <Modal.Footer />
      </Modal>

      <style>{`
        .transaction-page {
          width: 100%;
          padding-inline: 0;
        }

        .transaction-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: nowrap;
        }

        .transaction-heading-wrap {
          min-width: 0;
          flex: 1 1 auto;
        }

        .transaction-title {
          font-size: clamp(1.4rem, 2vw, 2rem);
          line-height: 1.2;
          word-break: break-word;
        }

        .transaction-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.75rem;
          flex: 0 0 auto;
          flex-wrap: nowrap;
          white-space: nowrap;
        }

        .transaction-search {
          width: 100%;
          max-width: 550px;
        }

        .transaction-search .input-group-text,
        .transaction-search .form-control {
          min-height: 48px;
          height: 48px;
        }

        .transaction-search .input-group-text {
          display: flex;
          align-items: center;
        }

        .transaction-action-btn {
          white-space: nowrap;
          min-height: 48px;
          padding-inline: 18px;
        }

        .transaction-list-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          flex-wrap: nowrap;
        }

        .transaction-list-title-wrap {
          min-width: 0;
          flex: 1 1 auto;
        }

        .transaction-table thead th {
          white-space: nowrap;
        }

        .detail-box {
          background: #f8fafc;
          border: 1px solid rgba(0, 0, 0, 0.06);
          border-radius: 0.85rem;
          padding: 14px 16px;
          height: 100%;
          word-break: break-word;
        }

        .course-page-btn {
          width: 2rem;
          height: 2rem;
          padding: 0;
          border-radius: 0.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .course-page-btn svg {
          font-size: 0.95rem;
        }

        @media (max-width: 767.98px) {
          .transaction-header {
            flex-wrap: wrap;
          }

          .transaction-heading-wrap {
            flex: 1 1 100%;
            width: 100%;
          }

          .transaction-header-actions {
            width: 100%;
            justify-content: flex-end;
          }

          .transaction-search {
            max-width: none;
            width: 100%;
          }

          .transaction-table {
            min-width: 760px;
          }

          .transaction-list-top {
            align-items: center;
            flex-wrap: wrap;
          }

          .transaction-list-title-wrap {
            flex: 1 1 auto;
            min-width: 0;
          }

          .transaction-list-top .transaction-action-btn {
            margin-left: auto;
          }
        }

        @media (max-width: 575.98px) {
          .transaction-header {
            padding-top: 0.5rem !important;
            padding-bottom: 1rem !important;
          }

          .transaction-title {
            font-size: 1.35rem;
          }

          .transaction-header-actions {
            gap: 0.45rem;
          }

          .transaction-action-btn {
            min-height: 42px;
            padding-inline: 14px;
            font-size: 0.92rem;
          }

          .transaction-search .input-group-text,
          .transaction-search .form-control {
            min-height: 42px;
            height: 42px;
          }

          .transaction-list-top {
            align-items: center;
            flex-wrap: wrap;
          }

          .transaction-list-title-wrap {
            flex: 1 1 auto;
            min-width: 0;
          }

          .transaction-list-top .transaction-action-btn {
            margin-left: auto;
          }
        }
      `}</style>
    </>
  );
}